import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Optional,
  Output,
} from '@angular/core';
import {
  CloseModal,
  ModalCloseMethod,
  OpenModal,
} from '@rivethealth/data/analytics/account-analytics-events';
import { AccountAnalyticsService } from '../../app/account/account-analytics.service';
import { ModalContextService } from './modal-escape.directive';

@Component({
  templateUrl: './modal.component.html',
  selector: 'modal',
  styleUrls: ['./modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalComponent {
  constructor(
    private readonly modalContext: ModalContextService,
    @Optional() private readonly analyticsService: AccountAnalyticsService,
    private readonly elementRef: ElementRef,
    private _cdr: ChangeDetectorRef,
  ) {}

  // We share this with the parent in ngOnInit so the parent can later tell us
  // what the close method is, when it closes this modal. We have to send this
  // up as a wrapper, rather than having the parent send the close method down
  // as a property, because this modal gets destroyed before it can propagate.
  readonly closeMethod: { method: ModalCloseMethod } = { method: 'Unknown' };

  @Input() compact = false;
  @Input() fullPage = false;
  @Input() noScroll = false;

  private _title: string;
  private titleTimeout: any;
  private TITLE_CHANGE_DELAY = 300;
  @Input()
  set title(value: string) {
    if (!this._title) {
      // Set immediately if this is the first time
      this._title = value;
    } else {
      // Delay subsequent updates
      clearTimeout(this.titleTimeout);
      this.titleTimeout = setTimeout(() => {
        this._title = value;
        this._cdr.markForCheck();
      }, this.TITLE_CHANGE_DELAY);
    }
  }
  get title(): string {
    return this._title;
  }

  @Input() outerWidth: string | undefined;
  @Input() width: string | undefined;
  @Output() close = new EventEmitter();
  @Output() closeMethodRef = new EventEmitter<typeof this.closeMethod>();
  @Input() allowOverflow: boolean = false;
  @Input() overrideFooter: boolean = false;
  @Input() wide: boolean = false;
  @HostBinding('class.background-shading') @Input() backgroundShading = true;

  private modalLayer: number;
  @HostListener('document:keydown.escape', ['$event']) escapeKey(
    event: KeyboardEvent,
  ) {
    if (this.modalContext.active === this.modalLayer) {
      event.stopPropagation();
      this.setCloseMethod('Press escape key');
      this.close.emit();
    }
  }

  ngOnInit() {
    this.closeMethodRef.emit(this.closeMethod);

    this.modalContext.pushContext();
    this.modalLayer = this.modalContext.active;

    // For when *ngIf/ngSwitchCase changes
    this.analyticsCheckVisibility();
  }

  ngAfterViewChecked() {
    // For when [class.hide/hidden/rv-hidden/rv-invisible] changes
    this.analyticsCheckVisibility();
  }

  cachedIsVisible = false;

  getVisibility(): boolean {
    const computedStyle = getComputedStyle(this.elementRef.nativeElement);
    const isVisible =
      computedStyle.getPropertyValue('visibility') !== 'hidden' &&
      computedStyle.getPropertyValue('display') !== 'none';
    return isVisible;
  }

  analyticsCheckVisibility() {
    const isVisible = this.getVisibility();

    if (this.analyticsService) {
      // This catches both *ngIf/ngSwitchCase and CSS visibility changes
      if (!this.cachedIsVisible && isVisible) {
        this.analyticsService.logEvent(OpenModal, {
          'modal title': this.title,
        });
      }
      // This catches only CSS visibility changes. If this component is
      // destroyed by *ngIf/ngSwitchCase, it will be caught by ngOnDestroy.
      if (this.cachedIsVisible && !isVisible) {
        this.analyticsService.logEvent(CloseModal, {
          'modal title': this.title,
          'close method': this.closeMethod.method,
        });
      }
    }

    this.cachedIsVisible = isVisible;
  }

  setCloseMethod(method: ModalCloseMethod) {
    this.closeMethod.method = method;
  }

  ngOnDestroy() {
    this.modalContext.popContext();

    // Skip firing close modal event if currently hidden
    if (this.analyticsService && this.getVisibility() === true) {
      this.analyticsService.logEvent(CloseModal, {
        'modal title': this.title,
        'close method': this.closeMethod.method,
      });
    }
    clearTimeout(this.titleTimeout);
  }
}
